// ═══════════════════════════════════════════════════════════════════════
// Part 8+9+10+11+12+13+14+15+16+17: Export Panel
// ═══════════════════════════════════════════════════════════════════════
// Modal/panel for configuring and executing the final export.
// Shows validation results, export summary, and download button.
// Part 9: Added notes field, save export run on successful download,
// and template default prefilling.
// Part 10: Added re-invoicing prevention — checks job_invoice_records
// for already-invoiced jobs and warns/excludes them.
// Part 11: Fixed excludeFromExport columns appearing in export.
//          Added blank row separators between invoice number groups.
// Part 12: Enhanced re-invoicing override — per-job selection, override
//          reason field, confirmation step, and improved UX.
// Part 13: Re-export is now file-only — no duplicate invoice records
//          are created. Existing invoice numbers are preserved. The
//          re-export just regenerates the file (e.g. after template or
//          column changes). Export run is still logged for audit.
// Part 14: Fixed re-export blocking — when re-exporting, synthetic
//          invoice assignments are built from existing job_invoice_records
//          so that validation passes and invoice columns are populated
//          correctly in the output file.
// Part 15: Fixed blank-row separator default for re-exports.
//          On dialog open, `alreadyInvoiced` has not been fetched yet,
//          so the toggle defaulted to OFF when only synthetic re-export
//          assignments would be present. Now a second effect seeds the
//          toggle default the first time `mergedInvoiceAssignments`
//          becomes non-empty (after the re-export check completes and
//          the user selects an override mode), without clobbering
//          manual user toggles during the session.
// Part 17: Generalized blank-row separator to work for ALL exports,
//          not just those with invoice assignments. The toggle is now
//          always visible, and a "Group By" dropdown lets users pick
//          any source field (Account, Pickup Date, Passenger, etc.) to
//          group on. When invoice assignments exist, defaults to
//          "Invoice Number" (preserving legacy behaviour).
// Part 18: Added "Continue without invoice numbers" bypass checkbox so
//          that users can re-export previously-invoiced rows or run a
//          test export even when the template uses invoice fields but
//          no fresh invoice assignments have been created. When checked,
//          the MISSING_INVOICES validation error is downgraded to a
//          warning, allowing the export to proceed. Any existing invoice
//          numbers from job_invoice_records are still populated via the
//          synthetic `mergedInvoiceAssignments`. Rows without any record
//          will simply have blank invoice columns.
import React, { useState, useMemo, useCallback, useEffect } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Download, FileSpreadsheet, FileText, Shield, AlertTriangle, CheckCircle2,
  XCircle, Loader2, Hash, TrendingUp, BarChart3, Info, ChevronDown, ChevronRight,
  FileCheck, AlertCircle, StickyNote, Receipt, ShieldAlert, Ban, EyeOff, Rows3,
  RefreshCw, ShieldCheck, CheckSquare, Pencil,
} from 'lucide-react';

import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/dates';
import type {
  ExportJobRow, ExportTemplateColumn, AddressAliasRule,
  MarginRule, InvoiceAssignment, InvoiceRunSummary,
  RowAnomalyResult, AnomalySummary, ExportTemplate,
  AnomalyConfig, ExportRunInput,
  InvoiceRunSettings as IInvoiceRunSettings,
} from './types';
import type { TownClassification } from './types';

import {
  type ExportSettings, type ExportFileType, type ExportScope, type ValidationResult,
  type ExportSummaryData,
  EXPORT_SCOPE_INFO,
  applyExportScope,
  buildExportRows,
  getExportHeaders,
  validateExport,
  buildExportSummary,
  generateCSV,
  generateXLSX,
  downloadFile,
  getDefaultFileBaseName,
  insertInvoiceGroupSeparators,
  insertFieldGroupSeparators,
  sortExportRowsByInvoice,
} from './exportEngine';


import { deriveInvoicePassengerName } from './transforms';

// ─── Part 17: Blank-Row Group-By Options ────────────────────────────
// Options for the "Group By" dropdown that controls which field is used
// to insert blank rows between groups in the export file. Each option
// maps to a field name on ExportJobRow (or the special 'invoice' value
// which triggers invoice-number-based grouping via existing logic).

export interface BlankRowGroupOption {
  value: string;       // 'invoice' | field name on ExportJobRow
  label: string;       // Human-readable label for the dropdown
  description: string; // Short helper text
  requiresInvoices?: boolean; // If true, only enabled when invoices assigned
}

const BLANK_ROW_GROUP_OPTIONS: BlankRowGroupOption[] = [
  { value: 'invoice', label: 'Invoice Number', description: 'One blank row between each invoice group (also sorts by invoice #)', requiresInvoices: true },
  { value: 'account_id', label: 'Account ID', description: 'Blank row between each account' },
  { value: 'account_name', label: 'Account Name', description: 'Blank row between each account name' },
  { value: 'pu_date', label: 'Pickup Date', description: 'Blank row between each pickup date' },
  { value: 'passenger_name', label: 'Passenger Name', description: 'Blank row between each passenger' },
  { value: 'payment_type', label: 'Payment Type', description: 'Blank row between each payment type' },
  { value: 'driver_name', label: 'Driver', description: 'Blank row between each driver' },
  { value: 'vehicle_id', label: 'Vehicle', description: 'Blank row between each vehicle' },
];



// ─── Re-invoicing Check Types ───────────────────────────────────────

interface AlreadyInvoicedInfo {
  tc_id: string;
  job_id: string;
  invoice_number: number;
  invoice_group_key: string | null;
  template_name: string | null;
  target_system: string | null;
  invoiced_at: string;
}


/** Override mode for re-export */
type ReinvoiceOverrideMode = 'exclude_all' | 'include_selected' | 'include_all';

// ─── Props ──────────────────────────────────────────────────────────

interface ExportPanelProps {
  open: boolean;
  onClose: () => void;
  rows: ExportJobRow[];
  columns: ExportTemplateColumn[];
  aliasRules: AddressAliasRule[];
  marginRules: MarginRule[];
  invoiceAssignments: Map<string, InvoiceAssignment> | null;
  invoiceSummary: InvoiceRunSummary | null;
  anomalyResults: Map<string, RowAnomalyResult> | null;
  anomalySummary: AnomalySummary | null;
  template: ExportTemplate | null;
  // Part 9: Additional context for saving export runs
  invoiceSettings?: IInvoiceRunSettings | null;
  anomalyConfig?: AnomalyConfig | null;
  filtersSnapshot?: any;
  onExportComplete?: () => void;
  townClassifications?: Map<string, TownClassification>;
  /** Total matching rows across all pages (for pagination warning) */
  totalRowCount?: number;
}



// ─── Component ──────────────────────────────────────────────────────

const ExportPanel: React.FC<ExportPanelProps> = ({
  open, onClose, rows, columns, aliasRules, marginRules,
  invoiceAssignments, invoiceSummary, anomalyResults, anomalySummary, template,
  invoiceSettings, anomalyConfig, filtersSnapshot, onExportComplete, townClassifications,
  totalRowCount,
}) => {


  // Settings state
  const [fileType, setFileType] = useState<ExportFileType>(
    (template?.default_file_type as ExportFileType) || 'csv'
  );
  const [fileName, setFileName] = useState('');
  const [scope, setScope] = useState<ExportScope>('all');
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [sheetName, setSheetName] = useState('Export');
  const [notes, setNotes] = useState('');
  const [blankRowBetweenInvoices, setBlankRowBetweenInvoices] = useState(false);
  // Part 17: Field name used to group rows when inserting blank-row separators.
  // 'invoice' → use invoice assignments (legacy behaviour, also sorts by invoice #)
  // <field>   → any ExportJobRow field (account_id, pu_date, passenger_name, …)
  const [blankRowGroupByField, setBlankRowGroupByField] = useState<string>('invoice');
  // Part 15: Tracks whether the blank-row toggle default has been seeded
  // during the current dialog-open session. Reset to false on open; set to
  // true the first time the effect below seeds a default, so subsequent
  // changes to `mergedInvoiceAssignments` don't clobber manual user toggles.
  const [blankRowDefaultSeeded, setBlankRowDefaultSeeded] = useState(false);

  // ─── Part 18: Continue Without Invoice Numbers (Bypass) ─────────
  // When true, the MISSING_INVOICES validation error is downgraded to a
  // warning so the user can proceed with:
  //   • Re-exporting previously-invoiced rows (whose numbers will still
  //     be populated via `mergedInvoiceAssignments` from job_invoice_records)
  //   • A test/dry-run export where invoice columns are intentionally blank
  // Resets to false on every dialog open so it doesn't persist between
  // sessions or template switches.
  const [continueWithoutInvoices, setContinueWithoutInvoices] = useState(false);

  // Validation & export state
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [summary, setSummary] = useState<ExportSummaryData | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState(false);
  const [validationExpanded, setValidationExpanded] = useState(true);
  const [summaryExpanded, setSummaryExpanded] = useState(true);

  // ─── Re-export Check State (Enhanced Part 12 + Part 13) ─────────
  const [alreadyInvoiced, setAlreadyInvoiced] = useState<AlreadyInvoicedInfo[]>([]);
  const [checkingInvoiced, setCheckingInvoiced] = useState(false);
  const [reinvoiceCheckDone, setReinvoiceCheckDone] = useState(false);
  const [reinvoiceSectionExpanded, setReinvoiceSectionExpanded] = useState(true);
  // Override state
  const [overrideMode, setOverrideMode] = useState<ReinvoiceOverrideMode>('exclude_all');
  const [selectedOverrideIds, setSelectedOverrideIds] = useState<Set<string>>(new Set());
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideConfirmed, setOverrideConfirmed] = useState(false);

  // ─── Job Selection State (pick which jobs to include in the export) ─────
  // selectedJobIds is a Set of tc_ids that the user has chosen to include.
  // When null, ALL rows are included (default — no active selection filter).
  // When a Set, only rows whose tc_id is in the set are included.
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string> | null>(null);
  const [jobSelectionExpanded, setJobSelectionExpanded] = useState(false);
  const [jobSelectionSearch, setJobSelectionSearch] = useState('');


  // Derived: is any override active?
  const isOverrideActive = overrideMode !== 'exclude_all';
  const hasOverrideReason = overrideReason.trim().length > 0;

  // Set of tc_ids that are already invoiced (for the same template/target)
  const alreadyInvoicedSet = useMemo(() => {
    return new Set(alreadyInvoiced.map(a => a.tc_id));
  }, [alreadyInvoiced]);

  // Set of tc_ids that should be EXCLUDED from export (based on override mode)
  const excludedInvoicedSet = useMemo(() => {
    if (overrideMode === 'include_all') return new Set<string>(); // nothing excluded
    if (overrideMode === 'include_selected') {
      // Exclude all EXCEPT the ones the user selected to override
      const excluded = new Set<string>();
      for (const a of alreadyInvoiced) {
        if (!selectedOverrideIds.has(a.tc_id)) {
          excluded.add(a.tc_id);
        }
      }
      return excluded;
    }
    // exclude_all: exclude everything
    return alreadyInvoicedSet;
  }, [overrideMode, alreadyInvoiced, selectedOverrideIds, alreadyInvoicedSet]);

  // Count of jobs being force-included (overridden)
  const overriddenCount = useMemo(() => {
    if (overrideMode === 'include_all') return alreadyInvoiced.length;
    if (overrideMode === 'include_selected') return selectedOverrideIds.size;
    return 0;
  }, [overrideMode, alreadyInvoiced.length, selectedOverrideIds.size]);

  // ─── Check for Already-Invoiced Jobs ────────────────────────────
  // Matches on both job_id (stable TaxiCaller ID) and tc_id (internal ID
  // that can change on re-import). Preferring job_id ensures previously
  // invoiced jobs are correctly identified even when the internal tc_id
  // has changed due to a TaxiCaller re-import.
  const checkAlreadyInvoiced = useCallback(async () => {
    if (!template || rows.length === 0) {
      setAlreadyInvoiced([]);
      setReinvoiceCheckDone(true);
      return;
    }

    setCheckingInvoiced(true);
    try {
      const BATCH_SIZE = 500;
      const jobIds = Array.from(new Set(rows.map(r => r.job_id).filter(Boolean)));
      const tcIds = Array.from(new Set(rows.map(r => r.tc_id).filter(Boolean)));
      const rawResults: any[] = [];

      // Helper to apply the template/target filter to a query
      const applyContextFilter = (query: any) => {
        if (template.template_name) {
          query = query.eq('template_name', template.template_name);
        }
        if (template.target_system) {
          query = query.eq('target_system', template.target_system);
        }
        return query;
      };

      // Query 1: Match on job_id (primary — stable TaxiCaller ID)
      for (let i = 0; i < jobIds.length; i += BATCH_SIZE) {
        const batch = jobIds.slice(i, i + BATCH_SIZE);
        let query = supabase
          .from('job_invoice_records')
          .select('tc_id, job_id, invoice_number, invoice_group_key, template_name, target_system, invoiced_at')
          .in('job_id', batch);
        query = applyContextFilter(query);
        const { data, error } = await query;
        if (error) throw error;
        if (data) rawResults.push(...data);
      }

      // Query 2: Match on tc_id (fallback — covers records where job_id may be null
      // or for backward-compatibility with older records)
      for (let i = 0; i < tcIds.length; i += BATCH_SIZE) {
        const batch = tcIds.slice(i, i + BATCH_SIZE);
        let query = supabase
          .from('job_invoice_records')
          .select('tc_id, job_id, invoice_number, invoice_group_key, template_name, target_system, invoiced_at')
          .in('tc_id', batch);
        query = applyContextFilter(query);
        const { data, error } = await query;
        if (error) throw error;
        if (data) rawResults.push(...data);
      }

      // Build lookups by job_id and tc_id — keep latest per identifier
      const byJobId = new Map<string, any>();
      const byTcId = new Map<string, any>();
      for (const r of rawResults) {
        if (r.job_id) {
          const existing = byJobId.get(r.job_id);
          if (!existing || new Date(r.invoiced_at) > new Date(existing.invoiced_at)) {
            byJobId.set(r.job_id, r);
          }
        }
        if (r.tc_id) {
          const existing = byTcId.get(r.tc_id);
          if (!existing || new Date(r.invoiced_at) > new Date(existing.invoiced_at)) {
            byTcId.set(r.tc_id, r);
          }
        }
      }

      // Match each current row against the lookups, preferring job_id over tc_id.
      // Deduplicate: each current row maps to at most one AlreadyInvoicedInfo,
      // keyed by the row's tc_id for consistent UI rendering.
      const matchByTcId = new Map<string, AlreadyInvoicedInfo>();
      for (const row of rows) {
        const match = (row.job_id && byJobId.get(row.job_id)) || (row.tc_id && byTcId.get(row.tc_id));
        if (match) {
          matchByTcId.set(row.tc_id, {
            tc_id: row.tc_id,
            job_id: row.job_id,
            invoice_number: match.invoice_number,
            invoice_group_key: match.invoice_group_key,
            template_name: match.template_name,
            target_system: match.target_system,
            invoiced_at: match.invoiced_at,
          });
        }
      }

      setAlreadyInvoiced(Array.from(matchByTcId.values()));
      setReinvoiceCheckDone(true);
    } catch (err: any) {
      console.error('Failed to check already-invoiced jobs:', err);
      toast({
        title: 'Warning',
        description: 'Could not check for previously exported jobs. Proceed with caution.',
        variant: 'destructive',
      });
      setReinvoiceCheckDone(true);
    } finally {
      setCheckingInvoiced(false);
    }
  }, [template, rows]);


  // Reset state when dialog opens — prefill from template defaults

  useEffect(() => {
    if (open && template) {
      setFileType((template.default_file_type as ExportFileType) || 'csv');
      setScope((template.default_export_scope as ExportScope) || 'all');
      setIncludeHeaders(template.default_include_headers ?? true);
      setSheetName(template.default_sheet_name || template.template_name?.substring(0, 31) || 'Export');
      setNotes(template.default_notes_template || '');
      // Part 15: Default blank row separators to ON when invoices are assigned.
      // If the parent already has `invoiceAssignments` at open time, seed
      // immediately and mark as seeded. If not, leave OFF for now and let the
      // follow-up effect below seed it once `mergedInvoiceAssignments` becomes
      // non-empty (which happens after the re-export check completes and the
      // user selects an override mode).
      const hasInvoiceAssignments = (invoiceAssignments && invoiceAssignments.size > 0);
      setBlankRowBetweenInvoices(!!hasInvoiceAssignments);
      setBlankRowDefaultSeeded(!!hasInvoiceAssignments);
      // Part 17: Default group-by field — invoice when invoices exist,
      // otherwise account_id as a sensible non-invoice default.
      setBlankRowGroupByField(hasInvoiceAssignments ? 'invoice' : 'account_id');
      setValidation(null);
      setSummary(null);
      // Part 18: Reset bypass to OFF on every dialog open
      setContinueWithoutInvoices(false);


      setExported(false);
      setAlreadyInvoiced([]);
      setReinvoiceCheckDone(false);
      // Reset override state
      setOverrideMode('exclude_all');
      setSelectedOverrideIds(new Set());
      setOverrideReason('');
      setOverrideConfirmed(false);

      // Reset job selection — default to null (ALL jobs included)
      setSelectedJobIds(null);
      setJobSelectionExpanded(false);
      setJobSelectionSearch('');


      // Set default file name
      const { included } = applyExportScope(rows, (template.default_export_scope as ExportScope) || 'all', anomalyResults);
      setFileName(getDefaultFileBaseName(template.template_name || 'export', included.length));

      // Check for already-invoiced jobs
      checkAlreadyInvoiced();
    }
  }, [open, template, rows, anomalyResults, checkAlreadyInvoiced]);


  // Note: The Part 15 "seed blank-row toggle default for re-exports" effect
  // has been moved below the `mergedInvoiceAssignments` useMemo declaration
  // to avoid a Temporal Dead Zone (TDZ) error — the effect references
  // `mergedInvoiceAssignments` in its dependency array, so that variable
  // must be declared first. See the useEffect after `hasInvoices`.




  // ─── Effective rows ─────────────────────────────────────────────
  // Apply two filter layers:
  //   1. Exclude "already invoiced" jobs based on the override mode.
  //   2. Apply the user's job-selection filter (selectedJobIds) — when null,
  //      ALL rows are included (default). When a Set, only rows whose
  //      tc_id is in the set are included.
  const effectiveRows = useMemo(() => {
    let out = rows;
    if (excludedInvoicedSet.size > 0) {
      out = out.filter(r => !excludedInvoicedSet.has(r.tc_id));
    }
    if (selectedJobIds !== null) {
      out = out.filter(r => selectedJobIds.has(r.tc_id));
    }
    return out;
  }, [rows, excludedInvoicedSet, selectedJobIds]);


  // Compute scoped row count
  const scopedInfo = useMemo(() => {
    const { included, excluded } = applyExportScope(effectiveRows, scope, anomalyResults);
    return { count: included.length, excluded };
  }, [effectiveRows, scope, anomalyResults]);

  // Update file name when scope changes
  useEffect(() => {
    if (template && open) {
      setFileName(getDefaultFileBaseName(template.template_name || 'export', scopedInfo.count));
    }
  }, [scopedInfo.count]);

  // Count of columns that will actually appear in the export (excluding excludeFromExport)
  const exportableColCount = useMemo(
    () => columns.filter(c => c.is_enabled && !c.transform_config?.excludeFromExport).length,
    [columns]
  );

  // Count of columns excluded from export
  const excludedColCount = useMemo(
    () => columns.filter(c => c.is_enabled && c.transform_config?.excludeFromExport).length,
    [columns]
  );

  // ─── Part 14: Merged Invoice Assignments for Re-export ────────────
  // When re-exporting, build synthetic InvoiceAssignment objects from
  // the existing job_invoice_records so that:
  // 1) Validation sees invoice assignments → no MISSING_INVOICES error
  // 2) buildExportRows populates invoice columns with correct numbers
  // 3) Blank row separators work correctly for re-exported jobs
  const mergedInvoiceAssignments = useMemo((): Map<string, InvoiceAssignment> | null => {
    // Start with the original assignments (from Invoice Run Settings)
    const merged = new Map<string, InvoiceAssignment>(
      invoiceAssignments ? invoiceAssignments : []
    );

    // If override is active, add synthetic assignments for re-exported jobs
    if (overriddenCount > 0) {
      // Build a lookup from rows for deriving passenger names
      const rowLookup = new Map<string, ExportJobRow>();
      for (const r of rows) {
        rowLookup.set(r.tc_id, r);
      }

      for (const ai of alreadyInvoiced) {
        // Only add for jobs that are being re-exported (not excluded)
        if (excludedInvoicedSet.has(ai.tc_id)) continue;
        // Don't overwrite if there's already a fresh assignment from Invoice Run Settings
        if (merged.has(ai.tc_id)) continue;

        const row = rowLookup.get(ai.tc_id);
        const passengerName = row ? deriveInvoicePassengerName(row) : '';

        merged.set(ai.tc_id, {
          rowId: ai.tc_id,
          invoicePassengerName: passengerName,
          invoiceGroupKey: ai.invoice_group_key || `reexport::${ai.tc_id}`,
          assignedInvoiceNumber: ai.invoice_number,
        });
      }
    }

    return merged.size > 0 ? merged : invoiceAssignments;
  }, [invoiceAssignments, overriddenCount, alreadyInvoiced, excludedInvoicedSet, rows]);

  // Build a merged invoice summary that includes re-exported invoice numbers
  const mergedInvoiceSummary = useMemo((): InvoiceRunSummary | null => {
    if (!mergedInvoiceAssignments || mergedInvoiceAssignments.size === 0) {
      return invoiceSummary;
    }
    // If we have the original summary and no overrides, just use it
    if (overriddenCount === 0) return invoiceSummary;

    // Build a synthetic summary from the merged assignments
    const allNumbers: number[] = [];
    const groupCounts = new Map<string, number>();
    for (const [, a] of mergedInvoiceAssignments) {
      allNumbers.push(a.assignedInvoiceNumber);
      groupCounts.set(a.invoiceGroupKey, (groupCounts.get(a.invoiceGroupKey) || 0) + 1);
    }
    if (allNumbers.length === 0) return invoiceSummary;

    const minNum = Math.min(...allNumbers);
    const maxNum = Math.max(...allNumbers);

    return {
      startingNumber: minNum,
      endingNumber: maxNum,
      uniqueGroups: groupCounts.size,
      totalRows: mergedInvoiceAssignments.size,
      groupBreakdown: Array.from(groupCounts.entries()).map(([gk, count]) => ({
        invoiceNumber: 0, // not critical for summary display
        groupKey: gk,
        rowCount: count,
        accountId: null,
        passengerName: null,
      })),
    };
  }, [mergedInvoiceAssignments, invoiceSummary, overriddenCount]);

  // Whether invoices are assigned — check merged (includes re-export synthetic assignments)
  const hasInvoices = (mergedInvoiceAssignments && mergedInvoiceAssignments.size > 0)
    || (invoiceAssignments && invoiceAssignments.size > 0);

  // ─── Part 15: Seed blank-row toggle default for re-exports ──────
  // When the dialog opens there are typically no fresh `invoiceAssignments`
  // and `alreadyInvoiced` has not been fetched yet, so the toggle above
  // defaults to OFF. Once the re-export check completes and the user picks
  // an override mode (include_all / include_selected), `mergedInvoiceAssignments`
  // populates and `hasInvoices` flips true — at which point we want the
  // toggle to default ON (consistent with the fresh-export behaviour).
  //
  // The `blankRowDefaultSeeded` flag ensures this only runs ONCE per dialog
  // session, so subsequent changes (e.g. the user manually toggles it off,
  // or changes the override mode which re-computes mergedInvoiceAssignments)
  // do NOT clobber the user's manual choice.
  //
  // IMPORTANT: This effect must be declared AFTER `mergedInvoiceAssignments`
  // to avoid a Temporal Dead Zone (TDZ) error.
  useEffect(() => {
    if (!open) return;
    if (blankRowDefaultSeeded) return;
    const mergedHasAssignments = mergedInvoiceAssignments && mergedInvoiceAssignments.size > 0;
    if (mergedHasAssignments) {
      setBlankRowBetweenInvoices(true);
      setBlankRowDefaultSeeded(true);
    }
  }, [open, blankRowDefaultSeeded, mergedInvoiceAssignments]);


  // ─── Part 18.1: Re-run validation when bypass toggles ────────────
  // When the user ticks/unticks the "Continue without invoice numbers"
  // checkbox, we need validation to re-run so the MISSING_INVOICES
  // error is downgraded to a warning (or restored) and the Download
  // button becomes enabled/disabled immediately.
  //
  // We only re-run if validation has already been executed at least
  // once (validation !== null) — otherwise the user hasn't clicked
  // "Run Export Validation" yet and we shouldn't run it preemptively.
  //
  // `handleValidate` is intentionally excluded from the dep array to
  // avoid an infinite loop (it depends on continueWithoutInvoices, so
  // including it would cause a new reference on every run). We use a
  // ref-like pattern via the validation existence check instead.
  useEffect(() => {
    if (!open) return;
    if (validation === null) return;
    // Re-run validation with the new bypass value
    handleValidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [continueWithoutInvoices]);



  // ─── Per-Job Override Helpers ───────────────────────────────────
  const toggleJobOverride = useCallback((tcId: string) => {
    setSelectedOverrideIds(prev => {
      const next = new Set(prev);
      if (next.has(tcId)) {
        next.delete(tcId);
      } else {
        next.add(tcId);
      }
      return next;
    });
    setValidation(null);
    setOverrideConfirmed(false);
  }, []);

  const selectAllOverrides = useCallback(() => {
    setSelectedOverrideIds(new Set(alreadyInvoiced.map(a => a.tc_id)));
    setValidation(null);
    setOverrideConfirmed(false);
  }, [alreadyInvoiced]);

  const deselectAllOverrides = useCallback(() => {
    setSelectedOverrideIds(new Set());
    setValidation(null);
    setOverrideConfirmed(false);
  }, []);

  // ─── Job Selection Helpers ──────────────────────────────────────
  // Rows eligible for selection = original rows minus any excluded by override mode.
  const selectableRows = useMemo(() => {
    if (excludedInvoicedSet.size === 0) return rows;
    return rows.filter(r => !excludedInvoicedSet.has(r.tc_id));
  }, [rows, excludedInvoicedSet]);

  const selectableRowTcIds = useMemo(
    () => new Set(selectableRows.map(r => r.tc_id).filter(Boolean)),
    [selectableRows]
  );

  // Whether a given row is currently "selected" — when selectedJobIds is null,
  // every selectable row is considered selected (default = all).
  const isJobSelected = useCallback((tcId: string) => {
    if (selectedJobIds === null) return true;
    return selectedJobIds.has(tcId);
  }, [selectedJobIds]);

  // Current selected count (for the header badge)
  const selectedJobCount = useMemo(() => {
    if (selectedJobIds === null) return selectableRows.length;
    // Count only jobs that are still in the selectable set
    let n = 0;
    for (const tc of selectedJobIds) {
      if (selectableRowTcIds.has(tc)) n++;
    }
    return n;
  }, [selectedJobIds, selectableRows.length, selectableRowTcIds]);

  const allJobsSelected = selectedJobIds === null || selectedJobCount === selectableRows.length;
  const noJobsSelected = selectedJobIds !== null && selectedJobCount === 0;

  const toggleJobSelection = useCallback((tcId: string) => {
    setSelectedJobIds(prev => {
      // If null (all selected), start from a full Set of selectable rows
      const base = prev === null ? new Set(selectableRowTcIds) : new Set(prev);
      if (base.has(tcId)) {
        base.delete(tcId);
      } else {
        base.add(tcId);
      }
      return base;
    });
    setValidation(null);
  }, [selectableRowTcIds]);

  const selectAllJobs = useCallback(() => {
    // null === "all selected" — preferred because it auto-expands as the row set changes
    setSelectedJobIds(null);
    setValidation(null);
  }, []);

  const clearAllJobSelection = useCallback(() => {
    setSelectedJobIds(new Set());
    setValidation(null);
  }, []);

  // Filtered job list for the selection UI (by search text)
  const filteredSelectableRows = useMemo(() => {
    const q = jobSelectionSearch.trim().toLowerCase();
    if (!q) return selectableRows;
    return selectableRows.filter(r => {
      const fields = [
        r.job_id, r.account_id, r.account_name, r.passenger_name,
        r.pick_up_location, r.drop_off_location, r.reference,
      ];
      return fields.some(f => f && String(f).toLowerCase().includes(q));
    });
  }, [selectableRows, jobSelectionSearch]);


  // ─── Run Validation ─────────────────────────────────────────────
  const handleValidate = useCallback(() => {
    const settings: ExportSettings = { fileType, fileName, scope, includeHeaders, sheetName, blankRowBetweenInvoices, blankRowGroupByField };

    const { included, excluded } = applyExportScope(effectiveRows, scope, anomalyResults);
    const headers = getExportHeaders(columns);
    // Part 14: Use merged assignments so re-exported jobs get their invoice data
    const exportRows = buildExportRows(included, columns, aliasRules, marginRules, mergedInvoiceAssignments, townClassifications, filtersSnapshot?.dateTo);

    // Part 14: Pass merged assignments to validation so MISSING_INVOICES check passes
    const validationResult = validateExport(
      included, columns, exportRows, settings,
      mergedInvoiceAssignments, anomalyResults, anomalySummary
    );

    // ─── Part 18: "Continue without invoice numbers" bypass ────────
    // If the user has ticked the bypass checkbox AND the only blocking
    // error is MISSING_INVOICES, downgrade that error to a warning so
    // the export can proceed. This supports two scenarios:
    //   • Re-exporting previously-invoiced rows (numbers are still
    //     populated via job_invoice_records synthetic assignments)
    //   • Running a test/dry-run export with blank invoice columns
    if (continueWithoutInvoices) {
      const missingInvoicesIdx = validationResult.errors.findIndex(e => e.code === 'MISSING_INVOICES');
      if (missingInvoicesIdx !== -1) {
        const [removed] = validationResult.errors.splice(missingInvoicesIdx, 1);
        validationResult.warnings.push({
          severity: 'warning',
          code: 'MISSING_INVOICES_BYPASSED',
          message: 'Continuing without invoice numbers (bypass enabled)',
          detail: 'Invoice field columns may be blank for rows that have no invoice record. Any rows that were previously invoiced will still show their existing invoice number from the audit log.',
        });
        // Recompute isValid / hasBlockingErrors after the downgrade
        validationResult.hasBlockingErrors = validationResult.errors.length > 0;
        validationResult.isValid = !validationResult.hasBlockingErrors;
      }
    }

    // Add re-export warnings to validation
    if (alreadyInvoiced.length > 0) {
      if (overriddenCount > 0) {
        validationResult.warnings.push({
          severity: 'warning',
          code: 'REINVOICE_OVERRIDE',
          message: `${overriddenCount} previously exported job(s) will be re-exported`,
          detail: overrideMode === 'include_all'
            ? `All ${alreadyInvoiced.length} previously exported jobs are being re-included in this export. Their existing invoice numbers will be kept — no new invoice records will be created. Reason: "${overrideReason.trim() || 'No reason provided'}".`
            : `${overriddenCount} of ${alreadyInvoiced.length} previously exported jobs have been selectively re-included. Their existing invoice numbers will be kept — no new invoice records will be created. Reason: "${overrideReason.trim() || 'No reason provided'}".`,
        });
      }

      const stillExcludedCount = alreadyInvoiced.length - overriddenCount;
      if (stillExcludedCount > 0) {
        validationResult.warnings.push({
          severity: 'warning',
          code: 'REINVOICE_EXCLUDED',
          message: `${stillExcludedCount} previously exported job(s) excluded`,
          detail: `These jobs were excluded because they have already been exported for "${template?.template_name}" / "${template?.target_system}".`,
        });
      }
    }

    // Part 14: Use merged summary for the export summary display
    const summaryData = buildExportSummary(
      template?.template_name || 'Export', settings, exportRows, columns, included,
      mergedInvoiceSummary, marginRules, anomalyResults, excluded
    );

    setValidation(validationResult);
    setSummary(summaryData);
    setValidationExpanded(true);
    setSummaryExpanded(true);
  }, [fileType, fileName, scope, includeHeaders, sheetName, blankRowBetweenInvoices, blankRowGroupByField, effectiveRows, columns, aliasRules, marginRules, mergedInvoiceAssignments, mergedInvoiceSummary, anomalyResults, anomalySummary, template, alreadyInvoiced, overriddenCount, overrideMode, overrideReason, townClassifications, continueWithoutInvoices, filtersSnapshot?.dateTo]);



  // ─── Save Export Run + Invoice Records ───────────────────────────
  const saveExportRun = useCallback(async (summaryData: ExportSummaryData, excluded: number, includedRows: ExportJobRow[]) => {
    try {
      // Serialize anomaly config (convert Set to array)
      let anomalyConfigSnapshot: any = null;
      if (anomalyConfig) {
        anomalyConfigSnapshot = {
          ...anomalyConfig,
          enabledTypes: Array.from(anomalyConfig.enabledTypes),
        };
      }

      // Build re-export override snapshot
      const reinvoiceOverrideSnapshot = overriddenCount > 0 ? {
        mode: overrideMode,
        type: 'file_only_reexport',  // Part 13: clarify this is file-only
        overriddenJobCount: overriddenCount,
        totalPreviouslyInvoiced: alreadyInvoiced.length,
        reason: overrideReason.trim() || null,
        overriddenJobIds: overrideMode === 'include_selected'
          ? Array.from(selectedOverrideIds)
          : alreadyInvoiced.map(a => a.tc_id),
        previousInvoiceNumbers: alreadyInvoiced
          .filter(a => overrideMode === 'include_all' || selectedOverrideIds.has(a.tc_id))
          .map(a => ({ tc_id: a.tc_id, job_id: a.job_id, prev_invoice: a.invoice_number, prev_date: a.invoiced_at })),
        note: 'No new invoice records were created — existing records preserved.',
      } : null;

      // Append override reason to notes if present
      let finalNotes = notes.trim() || null;
      if (overriddenCount > 0 && overrideReason.trim()) {
        const overrideNote = `[RE-EXPORT: ${overriddenCount} job(s) — ${overrideReason.trim()}]`;
        finalNotes = finalNotes ? `${overrideNote}\n${finalNotes}` : overrideNote;
      }

      const runInput: ExportRunInput = {
        template_id: template?.id || null,
        template_name_snapshot: template?.template_name || 'Unknown',
        target_system_snapshot: template?.target_system || null,
        file_type: fileType,
        file_name: `${fileName}.${fileType}`,
        sheet_name: fileType === 'xlsx' ? sheetName : null,
        export_scope: scope,
        include_headers: includeHeaders,
        starting_invoice_number: invoiceSummary?.startingNumber || null,
        ending_invoice_number: invoiceSummary?.endingNumber || null,
        invoice_group_count: invoiceSummary?.uniqueGroups || null,
        invoice_grouping_mode: invoiceSettings?.groupingMode || null,
        invoice_grouping_config: invoiceSettings ? { customGroupFields: invoiceSettings.customGroupFields } : null,
        row_count: summaryData.rowCount,
        column_count: summaryData.columnCount,
        flagged_row_count: anomalySummary?.totalFlagged || 0,
        reviewed_row_count: anomalySummary?.reviewed || 0,
        ignored_row_count: anomalySummary?.ignored || 0,
        excluded_row_count: excluded,
        margin_rule_count: marginRules.filter(r => r.is_active).length,
        grand_total_snapshot: summaryData.totalGrandTotal,
        filters_snapshot: filtersSnapshot ? { ...filtersSnapshot, reinvoice_override: reinvoiceOverrideSnapshot } : (reinvoiceOverrideSnapshot ? { reinvoice_override: reinvoiceOverrideSnapshot } : null),
        template_columns_snapshot: columns.map(c => ({
          id: c.id, export_column_name: c.export_column_name, source_type: c.source_type,
          source_field: c.source_field, is_enabled: c.is_enabled, is_required: c.is_required,
          transform_type: c.transform_type, column_order: c.column_order,
        })),
        margin_rules_snapshot: marginRules.filter(r => r.is_active).map(r => ({
          id: r.id, rule_name: r.rule_name, applies_to_field: r.applies_to_field,
          margin_type: r.margin_type, margin_value: r.margin_value,
          target_scope: r.target_scope, target_config: r.target_config,
        })),
        invoice_settings_snapshot: invoiceSettings || null,
        anomaly_config_snapshot: anomalyConfigSnapshot,
        notes: finalNotes,
        exported_by: 'admin',
      };

      // Insert export run and get back the ID
      const { data: runData, error: runError } = await supabase
        .from('export_runs')
        .insert(runInput)
        .select('id')
        .single();

      if (runError) throw runError;

      const exportRunId = runData?.id || null;

      // ─── Part 13: Only create invoice records for NEW jobs ─────
      // Jobs that are already invoiced (re-exported) do NOT get new
      // invoice records — their existing records are preserved.
      if (invoiceAssignments && invoiceAssignments.size > 0 && exportRunId) {
        const invoiceRecords: any[] = [];
        for (const row of includedRows) {
          // Skip jobs that are already invoiced — no duplicate records
          if (alreadyInvoicedSet.has(row.tc_id)) continue;

          const assignment = invoiceAssignments.get(row.tc_id);
          if (assignment) {
            invoiceRecords.push({
              tc_id: row.tc_id,
              job_id: row.job_id,
              invoice_number: assignment.assignedInvoiceNumber,
              invoice_group_key: assignment.invoiceGroupKey,
              export_run_id: exportRunId,
              template_name: template?.template_name || null,
              target_system: template?.target_system || null,
            });
          }
        }

        if (invoiceRecords.length > 0) {
          // Insert in batches of 500 to avoid payload limits
          const BATCH_SIZE = 500;
          for (let i = 0; i < invoiceRecords.length; i += BATCH_SIZE) {
            const batch = invoiceRecords.slice(i, i + BATCH_SIZE);
            const { error: invError } = await supabase
              .from('job_invoice_records')
              .insert(batch);
            if (invError) {
              console.error('Failed to save invoice records batch:', invError);
              toast({
                title: 'Warning',
                description: `Export logged but failed to save some invoice records (batch ${Math.floor(i / BATCH_SIZE) + 1}).`,
                variant: 'destructive',
              });
            }
          }
        }
      }
    } catch (err: any) {
      console.error('Failed to save export run:', err);
      toast({
        title: 'Warning',
        description: 'Export completed but failed to save history record. The file was downloaded successfully.',
        variant: 'destructive',
      });
    }
  }, [template, fileType, fileName, sheetName, scope, includeHeaders, invoiceSummary, invoiceSettings, anomalySummary, anomalyConfig, marginRules, columns, filtersSnapshot, notes, invoiceAssignments, overriddenCount, overrideMode, overrideReason, selectedOverrideIds, alreadyInvoiced, alreadyInvoicedSet]);


  // ─── Execute Export ─────────────────────────────────────────────
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const settings: ExportSettings = { fileType, fileName, scope, includeHeaders, sheetName, blankRowBetweenInvoices, blankRowGroupByField };
      const { included, excluded } = applyExportScope(effectiveRows, scope, anomalyResults);
      const headers = getExportHeaders(columns);
      // Part 14: Use merged assignments so re-exported jobs get their invoice data in the file
      const originalExportRows = buildExportRows(included, columns, aliasRules, marginRules, mergedInvoiceAssignments, townClassifications, filtersSnapshot?.dateTo);

      // Part 17: Apply blank-row grouping based on the user's choice.
      //
      // Priority order:
      //   1. Toggle OFF      → no separators, but still invoice-sort if invoices exist
      //   2. groupBy=invoice → legacy invoice grouping (sorts + separators)
      //   3. groupBy=<field> → generic field-based separators (no re-sort)
      //
      // Falls back to a sort-only behaviour when invoices exist and the
      // toggle is off, matching the pre-Part-17 UX.
      let finalExportRows = originalExportRows;
      const hasFreshInvoices = mergedInvoiceAssignments && mergedInvoiceAssignments.size > 0;

      if (blankRowBetweenInvoices) {
        if (blankRowGroupByField === 'invoice' && hasFreshInvoices) {
          // Legacy behaviour: sort by invoice + insert separators between invoice groups
          finalExportRows = insertInvoiceGroupSeparators(originalExportRows, included, mergedInvoiceAssignments!, headers);
        } else if (blankRowGroupByField && blankRowGroupByField !== 'invoice') {
          // Part 17: Generic field-based separators (no re-sort)
          finalExportRows = insertFieldGroupSeparators(originalExportRows, included, blankRowGroupByField, headers);
        } else if (hasFreshInvoices) {
          // groupBy is 'invoice' but no invoices present → just sort, no separators
          const { sortedExportRows } = sortExportRowsByInvoice(originalExportRows, included, mergedInvoiceAssignments);
          finalExportRows = sortedExportRows;
        }
      } else if (hasFreshInvoices) {
        // Toggle off but invoices exist → still sort by invoice number (pre-Part-17 behaviour)
        const { sortedExportRows } = sortExportRowsByInvoice(originalExportRows, included, mergedInvoiceAssignments);
        finalExportRows = sortedExportRows;
      }

      const fullFileName = `${fileName}.${fileType}`;

      if (fileType === 'csv') {
        const csvContent = generateCSV(headers, finalExportRows, includeHeaders);
        downloadFile(csvContent, fullFileName, 'text/csv;charset=utf-8');
      } else {
        const xlsxBuffer = generateXLSX(headers, finalExportRows, sheetName, includeHeaders);
        downloadFile(xlsxBuffer, fullFileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      }

      setExported(true);

      // Save export run to database — use merged summary for invoice range
      const summaryData = buildExportSummary(
        template?.template_name || 'Export', settings, originalExportRows, columns, included,
        mergedInvoiceSummary, marginRules, anomalyResults, excluded
      );
      await saveExportRun(summaryData, excluded, included);

      onExportComplete?.();
    } catch (err: any) {
      console.error('Export error:', err);
      toast({ title: 'Export Error', description: err.message || 'Failed to generate export', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  }, [fileType, fileName, scope, includeHeaders, sheetName, blankRowBetweenInvoices, blankRowGroupByField, effectiveRows, columns, aliasRules, marginRules, mergedInvoiceAssignments, mergedInvoiceSummary, anomalyResults, template, saveExportRun, onExportComplete, townClassifications, filtersSnapshot?.dateTo]);





  const canExport = validation && validation.isValid && !exporting;
  // Block export if override is active but not confirmed or no reason
  const overrideBlocksExport = isOverrideActive && overriddenCount > 0 && (!overrideConfirmed || !hasOverrideReason);
  const activeMarginCount = marginRules.filter(r => r.is_active).length;

  // Count of NEW jobs (not previously invoiced) in the effective rows
  const newJobCount = useMemo(() => {
    return effectiveRows.filter(r => !alreadyInvoicedSet.has(r.tc_id)).length;
  }, [effectiveRows, alreadyInvoicedSet]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Download className="w-5 h-5 text-primary" />
            Export Data
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-xs">
            Configure and download your export file using template "{template?.template_name || '?'}"
            {template?.is_official && (
              <Badge className="ml-2 bg-amber-900/30 text-amber-400 border-amber-700/40 text-[9px] px-1.5 py-0">Official</Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">

          {/* ═══ Previously Exported Check Section ═══ */}
          {reinvoiceCheckDone && alreadyInvoiced.length > 0 && (
            <div className={`border rounded-md overflow-hidden transition-colors ${
              overriddenCount > 0
                ? 'border-amber-700/50 shadow-[0_0_8px_rgba(245,158,11,0.1)]'
                : 'border-amber-700/40'
            }`}>
              {/* Section Header */}
              <button
                className={`w-full flex items-center justify-between px-3 py-2 transition-colors ${
                  overriddenCount > 0
                    ? 'bg-amber-900/15 hover:bg-amber-900/20'
                    : 'bg-amber-900/10 hover:bg-amber-900/15'
                }`}
                onClick={() => setReinvoiceSectionExpanded(!reinvoiceSectionExpanded)}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <ShieldAlert className={`w-4 h-4 ${overriddenCount > 0 ? 'text-amber-400' : 'text-amber-400'}`} />
                  <span className="text-xs font-semibold text-foreground">Previously Exported</span>
                  <Badge className="bg-amber-900/30 text-amber-400 border-amber-700/40 text-[9px] px-1.5 py-0">
                    {alreadyInvoiced.length} already exported
                  </Badge>
                  {overriddenCount === 0 && (
                    <Badge className="bg-green-900/30 text-green-400 border-green-700/40 text-[9px] px-1.5 py-0">
                      <Ban className="w-2.5 h-2.5 mr-0.5" /> Excluded
                    </Badge>
                  )}
                  {overriddenCount > 0 && overriddenCount < alreadyInvoiced.length && (
                    <>
                      <Badge className="bg-amber-900/30 text-amber-400 border-amber-700/40 text-[9px] px-1.5 py-0">
                        <RefreshCw className="w-2.5 h-2.5 mr-0.5" /> {overriddenCount} re-exporting
                      </Badge>
                      <Badge className="bg-green-900/30 text-green-400 border-green-700/40 text-[9px] px-1.5 py-0">
                        <Ban className="w-2.5 h-2.5 mr-0.5" /> {alreadyInvoiced.length - overriddenCount} excluded
                      </Badge>
                    </>
                  )}
                  {overriddenCount > 0 && overriddenCount === alreadyInvoiced.length && (
                    <Badge className="bg-amber-900/30 text-amber-400 border-amber-700/40 text-[9px] px-1.5 py-0">
                      <RefreshCw className="w-2.5 h-2.5 mr-0.5" /> All re-exporting
                    </Badge>
                  )}
                </div>
                {reinvoiceSectionExpanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
              </button>

              {reinvoiceSectionExpanded && (
                <div className="px-3 py-2.5 space-y-3">
                  {/* Warning Banner */}
                  <div className="flex items-start gap-2 rounded px-2.5 py-2 bg-amber-900/10 border border-amber-700/20">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-400" />
                    <div>
                      <p className="text-xs font-medium text-amber-400">
                        {alreadyInvoiced.length} job{alreadyInvoiced.length !== 1 ? 's' : ''} in this dataset {alreadyInvoiced.length !== 1 ? 'have' : 'has'} already been exported
                      </p>
                      <p className="text-[10px] mt-0.5 text-amber-400/70">
                        These jobs already have invoice records for template "{template?.template_name}" / target "{template?.target_system || 'Generic'}".
                        {overriddenCount === 0
                          ? ' They will be excluded from this export to prevent duplicates.'
                          : overriddenCount === alreadyInvoiced.length
                            ? ' All are being re-included — their existing invoice numbers will be kept. No new invoice records will be created.'
                            : ` ${overriddenCount} will be re-exported with their existing invoice numbers. ${alreadyInvoiced.length - overriddenCount} will be excluded.`
                        }
                      </p>
                    </div>
                  </div>

                  {/* ─── Override Mode Selector ─────────────────────── */}
                  <div className="space-y-2">
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Override Mode</Label>
                    <div className="grid grid-cols-3 gap-1.5">
                      {/* Exclude All (default, safe) */}
                      <button
                        className={`flex flex-col items-center gap-1 px-2 py-2 rounded border text-center transition-all ${
                          overrideMode === 'exclude_all'
                            ? 'bg-green-900/20 border-green-700/50 ring-1 ring-green-600/30'
                            : 'bg-secondary/20 border-border/50 hover:bg-secondary/30'
                        }`}
                        onClick={() => {
                          setOverrideMode('exclude_all');
                          setOverrideConfirmed(false);
                          setValidation(null);
                        }}
                      >
                        <Ban className={`w-4 h-4 ${overrideMode === 'exclude_all' ? 'text-green-400' : 'text-muted-foreground'}`} />
                        <span className={`text-[10px] font-medium ${overrideMode === 'exclude_all' ? 'text-green-400' : 'text-muted-foreground'}`}>
                          Exclude All
                        </span>
                        <span className="text-[8px] text-muted-foreground/60">Safe — skip exported</span>
                      </button>

                      {/* Include Selected (per-job) */}
                      <button
                        className={`flex flex-col items-center gap-1 px-2 py-2 rounded border text-center transition-all ${
                          overrideMode === 'include_selected'
                            ? 'bg-amber-900/20 border-amber-700/50 ring-1 ring-amber-600/30'
                            : 'bg-secondary/20 border-border/50 hover:bg-secondary/30'
                        }`}
                        onClick={() => {
                          setOverrideMode('include_selected');
                          setOverrideConfirmed(false);
                          setValidation(null);
                        }}
                      >
                        <CheckSquare className={`w-4 h-4 ${overrideMode === 'include_selected' ? 'text-amber-400' : 'text-muted-foreground'}`} />

                        <span className={`text-[10px] font-medium ${overrideMode === 'include_selected' ? 'text-amber-400' : 'text-muted-foreground'}`}>
                          Select Jobs
                        </span>
                        <span className="text-[8px] text-muted-foreground/60">Pick which to re-export</span>
                      </button>

                      {/* Include All (full override) */}
                      <button
                        className={`flex flex-col items-center gap-1 px-2 py-2 rounded border text-center transition-all ${
                          overrideMode === 'include_all'
                            ? 'bg-amber-900/20 border-amber-700/50 ring-1 ring-amber-600/30'
                            : 'bg-secondary/20 border-border/50 hover:bg-secondary/30'
                        }`}
                        onClick={() => {
                          setOverrideMode('include_all');
                          setOverrideConfirmed(false);
                          setValidation(null);
                        }}
                      >
                        <RefreshCw className={`w-4 h-4 ${overrideMode === 'include_all' ? 'text-amber-400' : 'text-muted-foreground'}`} />
                        <span className={`text-[10px] font-medium ${overrideMode === 'include_all' ? 'text-amber-400' : 'text-muted-foreground'}`}>
                          Include All
                        </span>
                        <span className="text-[8px] text-muted-foreground/60">Re-export everything</span>
                      </button>
                    </div>
                  </div>

                  {/* ─── Job List with Checkboxes (Include Selected mode) ─── */}
                  {overrideMode === 'include_selected' && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground">
                          {selectedOverrideIds.size} of {alreadyInvoiced.length} selected for re-export
                        </span>
                        <div className="flex items-center gap-1.5">
                          <button
                            className="text-[9px] text-blue-400 hover:text-blue-300 underline"
                            onClick={selectAllOverrides}
                          >
                            Select All
                          </button>
                          <span className="text-muted-foreground/40 text-[9px]">|</span>
                          <button
                            className="text-[9px] text-blue-400 hover:text-blue-300 underline"
                            onClick={deselectAllOverrides}
                          >
                            Deselect All
                          </button>
                        </div>
                      </div>

                      <div className="max-h-[150px] overflow-y-auto border border-border/30 rounded">
                        <table className="w-full text-[10px]">
                          <thead className="sticky top-0 bg-card z-10">
                            <tr className="border-b border-border/40">
                              <th className="text-left py-1 px-2 text-muted-foreground font-medium w-6"></th>
                              <th className="text-left py-1 text-muted-foreground font-medium">Job ID</th>
                              <th className="text-center py-1 text-muted-foreground font-medium">Invoice #</th>
                              <th className="text-left py-1 text-muted-foreground font-medium">Exported At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {alreadyInvoiced.map(a => {
                              const isSelected = selectedOverrideIds.has(a.tc_id);
                              return (
                                <tr
                                  key={a.tc_id}
                                  className={`border-b border-border/20 cursor-pointer transition-colors ${
                                    isSelected
                                      ? 'bg-amber-900/10 hover:bg-amber-900/15'
                                      : 'hover:bg-secondary/20'
                                  }`}
                                  onClick={() => toggleJobOverride(a.tc_id)}
                                >
                                  <td className="py-0.5 px-2">
                                    <Checkbox
                                      checked={isSelected}
                                      onCheckedChange={() => toggleJobOverride(a.tc_id)}
                                      className="h-3 w-3"
                                    />
                                  </td>
                                  <td className={`py-0.5 font-mono ${isSelected ? 'text-amber-400' : 'text-foreground'}`}>
                                    {a.job_id}
                                  </td>
                                  <td className="py-0.5 text-center">
                                    <Badge className="bg-indigo-900/30 text-indigo-400 border-indigo-700/40 text-[9px] px-1 py-0 font-mono">
                                      {a.invoice_number}
                                    </Badge>
                                  </td>
                                  <td className="py-0.5 text-muted-foreground">
                                    {formatDate(a.invoiced_at)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* ─── Job List (read-only, for exclude_all and include_all) ─── */}
                  {overrideMode !== 'include_selected' && (
                    <div className="max-h-[120px] overflow-y-auto">
                      <table className="w-full text-[10px]">
                        <thead>
                          <tr className="border-b border-border/40">
                            <th className="text-left py-1 text-muted-foreground font-medium">Job ID</th>
                            <th className="text-center py-1 text-muted-foreground font-medium">Invoice #</th>
                            <th className="text-left py-1 text-muted-foreground font-medium">Exported At</th>
                            <th className="text-center py-1 text-muted-foreground font-medium w-16">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {alreadyInvoiced.slice(0, 20).map(a => (
                            <tr key={a.tc_id} className={`border-b border-border/20 ${
                              overrideMode === 'include_all' ? 'bg-amber-900/5' : ''
                            }`}>
                              <td className={`py-0.5 font-mono ${overrideMode === 'include_all' ? 'text-amber-400' : 'text-foreground'}`}>
                                {a.job_id}
                              </td>
                              <td className="py-0.5 text-center">
                                <Badge className="bg-indigo-900/30 text-indigo-400 border-indigo-700/40 text-[9px] px-1 py-0 font-mono">
                                  {a.invoice_number}
                                </Badge>
                              </td>
                              <td className="py-0.5 text-muted-foreground">
                                {formatDate(a.invoiced_at)}
                              </td>
                              <td className="py-0.5 text-center">
                                {overrideMode === 'include_all' ? (
                                  <span className="text-[8px] text-amber-400 font-medium">RE-EXPORT</span>
                                ) : (
                                  <span className="text-[8px] text-green-400/70 font-medium">EXCLUDED</span>
                                )}
                              </td>
                            </tr>
                          ))}
                          {alreadyInvoiced.length > 20 && (
                            <tr>
                              <td colSpan={4} className="py-1 text-muted-foreground/60 text-center italic">
                                ...and {alreadyInvoiced.length - 20} more
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* ─── Override Reason + Confirmation (when override is active) ─── */}
                  {isOverrideActive && overriddenCount > 0 && (
                    <div className="space-y-2.5 border-t border-border/30 pt-2.5">
                      {/* Override Reason (required) */}
                      <div>
                        <div className="flex items-center gap-1.5 mb-1">
                          <Pencil className="w-3 h-3 text-amber-400" />
                          <Label className="text-[10px] text-amber-400 uppercase tracking-wider font-semibold">
                            Re-export Reason
                          </Label>
                          <span className="text-[9px] text-amber-400/60">(required)</span>
                        </div>
                        <Textarea
                          value={overrideReason}
                          onChange={e => {
                            setOverrideReason(e.target.value);
                            setOverrideConfirmed(false);
                            setValidation(null);
                          }}
                          placeholder="e.g. Changed template columns, Updated address aliases, Fixed margin rules, Re-exporting after column reorder..."
                          className="dark-input text-xs min-h-[44px] resize-none border-amber-700/30 focus:border-amber-600/50"
                        />
                        {!hasOverrideReason && (
                          <p className="text-[9px] text-amber-400/60 mt-0.5">
                            You must provide a reason before re-exporting previously exported jobs
                          </p>
                        )}
                      </div>

                      {/* Confirmation Checkbox */}
                      {hasOverrideReason && (
                        <div
                          className={`flex items-start gap-2.5 rounded px-3 py-2 cursor-pointer transition-colors ${
                            overrideConfirmed
                              ? 'bg-green-900/15 border border-green-700/30'
                              : 'bg-amber-900/10 border border-amber-700/30 hover:bg-amber-900/15'
                          }`}
                          onClick={() => {
                            setOverrideConfirmed(!overrideConfirmed);
                            setValidation(null);
                          }}
                        >
                          <Checkbox
                            checked={overrideConfirmed}
                            onCheckedChange={(v) => {
                              setOverrideConfirmed(!!v);
                              setValidation(null);
                            }}
                            className="mt-0.5 h-4 w-4"
                          />
                          <div>
                            <p className="text-xs font-medium text-foreground">
                              I understand these jobs have been previously exported
                            </p>
                            <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                              {overriddenCount} job{overriddenCount !== 1 ? 's' : ''} will be re-exported using their existing invoice numbers.
                              No new invoice records will be created — the previous records remain unchanged.
                              This is a file-only re-export (e.g. after changing the template or columns).
                              The reason will be logged with the export run.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Override Status Summary */}
                      {overrideConfirmed && hasOverrideReason && (
                        <div className="flex items-center gap-2 bg-green-900/10 border border-green-700/20 rounded px-2.5 py-1.5">
                          <ShieldCheck className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                          <span className="text-[10px] text-green-400">
                            Override confirmed — {overriddenCount} job{overriddenCount !== 1 ? 's' : ''} will be re-exported.
                            No new invoice records. Reason will be logged.
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Re-export check in progress */}
          {checkingInvoiced && (
            <div className="flex items-center gap-2 bg-secondary/30 border border-border/50 rounded-md px-3 py-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Checking for previously exported jobs...</span>
            </div>
          )}

          {/* No re-export issues */}
          {reinvoiceCheckDone && alreadyInvoiced.length === 0 && invoiceAssignments && invoiceAssignments.size > 0 && (
            <div className="flex items-center gap-2 bg-green-900/10 border border-green-700/20 rounded-md px-3 py-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
              <span className="text-xs text-green-400">No previously exported jobs found — safe to proceed</span>
            </div>
          )}

          {/* ═══ Job Selection Section ════════════════════════════ */}
          {selectableRows.length > 0 && (
            <div className={`border rounded-md overflow-hidden ${
              selectedJobIds !== null && !allJobsSelected
                ? 'border-blue-700/50 shadow-[0_0_8px_rgba(59,130,246,0.08)]'
                : 'border-border/60'
            }`}>
              <button
                className={`w-full flex items-center justify-between px-3 py-2 transition-colors ${
                  selectedJobIds !== null && !allJobsSelected
                    ? 'bg-blue-900/10 hover:bg-blue-900/15'
                    : 'bg-secondary/20 hover:bg-secondary/30'
                }`}
                onClick={() => setJobSelectionExpanded(!jobSelectionExpanded)}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <CheckSquare className={`w-4 h-4 ${selectedJobIds !== null && !allJobsSelected ? 'text-blue-400' : 'text-muted-foreground'}`} />
                  <span className="text-xs font-semibold text-foreground">Select Jobs for Export</span>
                  {allJobsSelected ? (
                    <Badge className="bg-green-900/30 text-green-400 border-green-700/40 text-[9px] px-1.5 py-0">
                      <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
                      All {selectableRows.length} included
                    </Badge>
                  ) : noJobsSelected ? (
                    <Badge className="bg-red-900/30 text-red-400 border-red-700/40 text-[9px] px-1.5 py-0">
                      <XCircle className="w-2.5 h-2.5 mr-0.5" />
                      None selected
                    </Badge>
                  ) : (
                    <Badge className="bg-blue-900/30 text-blue-400 border-blue-700/40 text-[9px] px-1.5 py-0">
                      {selectedJobCount} of {selectableRows.length} selected
                    </Badge>
                  )}
                </div>
                {jobSelectionExpanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
              </button>

              {jobSelectionExpanded && (
                <div className="px-3 py-2.5 space-y-2">
                  {/* Description + Helper */}
                  <p className="text-[10px] text-muted-foreground">
                    Choose which jobs to include in this export. When all jobs are selected (the default) every row in the
                    filtered dataset will be exported. Use the checkboxes below to export only specific jobs.
                  </p>

                  {/* Top controls: Select All / Clear / Search */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={allJobsSelected ? true : (noJobsSelected ? false : 'indeterminate')}
                        onCheckedChange={(v) => {
                          if (v === true || v === 'indeterminate') {
                            selectAllJobs();
                          } else {
                            clearAllJobSelection();
                          }
                        }}
                        className="h-4 w-4"
                      />
                      <span className="text-xs text-foreground font-medium">
                        {allJobsSelected ? 'All selected' : noJobsSelected ? 'None selected' : `${selectedJobCount} selected`}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        className="text-[9px] text-blue-400 hover:text-blue-300 underline"
                        onClick={selectAllJobs}
                      >
                        Select All
                      </button>
                      <span className="text-muted-foreground/40 text-[9px]">|</span>
                      <button
                        className="text-[9px] text-blue-400 hover:text-blue-300 underline"
                        onClick={clearAllJobSelection}
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="flex-1 min-w-[160px]">
                      <Input
                        value={jobSelectionSearch}
                        onChange={e => setJobSelectionSearch(e.target.value)}
                        placeholder="Search job ID, account, passenger, address..."
                        className="dark-input text-[11px] h-7"
                      />
                    </div>
                  </div>

                  {/* Job list */}
                  <div className="max-h-[260px] overflow-y-auto border border-border/30 rounded">
                    {filteredSelectableRows.length === 0 ? (
                      <div className="px-3 py-4 text-center text-[11px] text-muted-foreground">
                        {jobSelectionSearch ? `No jobs match "${jobSelectionSearch}"` : 'No jobs available for selection'}
                      </div>
                    ) : (
                      <table className="w-full text-[10px]">
                        <thead className="sticky top-0 bg-card z-10">
                          <tr className="border-b border-border/40">
                            <th className="text-left py-1 px-2 text-muted-foreground font-medium w-6"></th>
                            <th className="text-left py-1 text-muted-foreground font-medium">Job ID</th>
                            <th className="text-left py-1 text-muted-foreground font-medium">Account</th>
                            <th className="text-left py-1 text-muted-foreground font-medium">Passenger</th>
                            <th className="text-left py-1 text-muted-foreground font-medium">Date</th>
                            <th className="text-right py-1 pr-2 text-muted-foreground font-medium">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredSelectableRows.slice(0, 500).map(r => {
                            const checked = isJobSelected(r.tc_id);
                            return (
                              <tr
                                key={r.tc_id}
                                className={`border-b border-border/20 cursor-pointer transition-colors ${
                                  checked ? 'hover:bg-blue-900/10' : 'bg-secondary/10 hover:bg-secondary/20'
                                }`}
                                onClick={() => toggleJobSelection(r.tc_id)}
                              >
                                <td className="py-0.5 px-2" onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={() => toggleJobSelection(r.tc_id)}
                                    className="h-3 w-3"
                                  />
                                </td>
                                <td className={`py-0.5 font-mono ${checked ? 'text-foreground' : 'text-muted-foreground/60'}`}>
                                  {r.job_id || '-'}
                                </td>
                                <td className={`py-0.5 ${checked ? 'text-foreground' : 'text-muted-foreground/60'}`}>
                                  <span className="font-mono">{r.account_id || '-'}</span>
                                  {r.account_name && <span className="text-muted-foreground/60 ml-1 text-[9px]">({r.account_name})</span>}
                                </td>
                                <td className={`py-0.5 ${checked ? 'text-foreground' : 'text-muted-foreground/60'} truncate max-w-[140px]`}>
                                  {r.passenger_name || '-'}
                                </td>
                                <td className="py-0.5 text-muted-foreground">
                                  {r.pu_date || '-'}
                                </td>
                                <td className={`py-0.5 text-right pr-2 font-mono ${checked ? 'text-foreground' : 'text-muted-foreground/60'}`}>
                                  {r.grand_total != null ? `$${Number(r.grand_total).toFixed(2)}` : '-'}
                                </td>
                              </tr>
                            );
                          })}
                          {filteredSelectableRows.length > 500 && (
                            <tr>
                              <td colSpan={6} className="py-1 text-muted-foreground/60 text-center italic text-[9px]">
                                Showing first 500 of {filteredSelectableRows.length} matching jobs. Use search to narrow down.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Active-filter indicator */}
                  {selectedJobIds !== null && !allJobsSelected && (
                    <div className="flex items-center gap-2 bg-blue-900/10 border border-blue-700/20 rounded px-2.5 py-1.5">
                      <Info className="w-3 h-3 text-blue-400 flex-shrink-0" />
                      <span className="text-[10px] text-blue-400">
                        Job selection filter active — only {selectedJobCount} of {selectableRows.length} eligible jobs will be exported.
                        {' '}
                        <button className="underline hover:text-blue-300" onClick={selectAllJobs}>
                          Reset to all
                        </button>
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}


          {/* ─── Export Settings ─────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-3">
            {/* File Type */}
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase">File Type</Label>
              <Select value={fileType} onValueChange={(v) => setFileType(v as ExportFileType)}>
                <SelectTrigger className="dark-input text-xs h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">
                    <span className="flex items-center gap-2">
                      <FileText className="w-3.5 h-3.5 text-green-400" />
                      CSV
                    </span>
                  </SelectItem>
                  <SelectItem value="xlsx">
                    <span className="flex items-center gap-2">
                      <FileSpreadsheet className="w-3.5 h-3.5 text-blue-400" />
                      XLSX (Excel)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Export Scope */}
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase">Row Scope</Label>
              <Select value={scope} onValueChange={(v) => { setScope(v as ExportScope); setValidation(null); }}>
                <SelectTrigger className="dark-input text-xs h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(EXPORT_SCOPE_INFO) as ExportScope[]).map(s => (
                    <SelectItem key={s} value={s}>
                      <span className="flex flex-col">
                        <span className="text-xs">{EXPORT_SCOPE_INFO[s].label}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[9px] text-muted-foreground/60 mt-0.5">{EXPORT_SCOPE_INFO[scope].description}</p>
            </div>
          </div>

          {/* File Name */}
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase">File Name</Label>
            <div className="flex items-center gap-1">
              <Input
                value={fileName}
                onChange={e => setFileName(e.target.value)}
                className="dark-input text-xs h-8 flex-1"
                placeholder="export_file_name"
              />
              <span className="text-xs text-muted-foreground">.{fileType}</span>
            </div>
          </div>

          {/* XLSX-specific options */}
          {fileType === 'xlsx' && (
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase">Sheet Name</Label>
              <Input
                value={sheetName}
                onChange={e => setSheetName(e.target.value.substring(0, 31))}
                className="dark-input text-xs h-8"
                placeholder="Sheet1"
                maxLength={31}
              />
              <p className="text-[9px] text-muted-foreground/60 mt-0.5">Max 31 characters</p>
            </div>
          )}

          {/* Include Headers */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs text-foreground">Include Column Headers</Label>
              <p className="text-[9px] text-muted-foreground/60">First row contains column names</p>
            </div>
            <Switch checked={includeHeaders} onCheckedChange={setIncludeHeaders} />
          </div>

          {/* ─── Part 17: Blank Row Between Groups (Always Available) ─── */}
          {/* Available for ALL exports, not just invoice-grouped ones.
              When invoices are assigned, defaults to invoice-number grouping
              (legacy behaviour). For non-invoice exports, the user picks
              another source field (Account, Pickup Date, Passenger, etc.). */}
          <div className="space-y-2 rounded-md border border-border/50 bg-secondary/10 px-3 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-2">
                <Rows3 className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                <div>
                  <Label className="text-xs text-foreground">Add Blank Row Between Groups</Label>
                  <p className="text-[9px] text-muted-foreground/60">
                    Insert an empty row whenever the selected field changes — makes the export easier to read in Excel.
                  </p>
                </div>
              </div>
              <Switch
                checked={blankRowBetweenInvoices}
                onCheckedChange={(v) => {
                  setBlankRowBetweenInvoices(v);
                  setValidation(null);
                }}
              />
            </div>

            {/* Group-By field picker — only shown when the toggle is ON */}
            {blankRowBetweenInvoices && (
              <div className="pl-6 space-y-1">
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Group By</Label>
                <Select
                  value={blankRowGroupByField}
                  onValueChange={(v) => {
                    setBlankRowGroupByField(v);
                    setValidation(null);
                  }}
                >
                  <SelectTrigger className="dark-input text-xs h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BLANK_ROW_GROUP_OPTIONS.map(opt => {
                      const disabled = !!opt.requiresInvoices && !hasInvoices;
                      return (
                        <SelectItem key={opt.value} value={opt.value} disabled={disabled}>
                          <span className="flex flex-col">
                            <span className="text-xs">
                              {opt.label}
                              {disabled && <span className="text-muted-foreground/60 ml-1">(requires invoices)</span>}
                            </span>
                            <span className="text-[9px] text-muted-foreground/60">{opt.description}</span>
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <p className="text-[9px] text-muted-foreground/60">
                  {blankRowGroupByField === 'invoice'
                    ? 'Rows will be sorted by invoice number, with a blank row between each invoice group.'
                    : `Rows keep their current order, and a blank row is inserted whenever "${BLANK_ROW_GROUP_OPTIONS.find(o => o.value === blankRowGroupByField)?.label || blankRowGroupByField}" changes.`
                  }
                </p>
              </div>
            )}
          </div>


          {/* Run Notes */}
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <StickyNote className="w-3 h-3 text-muted-foreground" />
              <Label className="text-[10px] text-muted-foreground uppercase">Run Notes</Label>
              <span className="text-[9px] text-muted-foreground/40">(optional)</span>
            </div>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Orbit March billing, ACC resubmission, 6030 split by passenger..."
              className="dark-input text-xs min-h-[50px] resize-none"
            />
          </div>

          {/* Quick Stats */}
          <div className="bg-secondary/30 border border-border/50 rounded-md px-3 py-2 flex flex-wrap items-center gap-3 text-xs">
            <div className="flex items-center gap-1.5">
              <BarChart3 className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Rows:</span>
              <span className="font-semibold text-foreground">{scopedInfo.count}</span>
              {scopedInfo.excluded > 0 && (
                <span className="text-amber-400 text-[10px]">({scopedInfo.excluded} excluded)</span>
              )}
              {excludedInvoicedSet.size > 0 && (
                <span className="text-orange-400 text-[10px]">({excludedInvoicedSet.size} prev. exported excluded)</span>
              )}
              {overriddenCount > 0 && (
                <span className="text-amber-400 text-[10px]">({overriddenCount} re-export)</span>
              )}
            </div>
            <div className="h-3 w-px bg-border" />
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">Columns:</span>
              <span className="font-semibold text-foreground ml-1">{exportableColCount}</span>
              {excludedColCount > 0 && (
                <span className="text-orange-400 text-[10px] flex items-center gap-0.5">
                  <EyeOff className="w-2.5 h-2.5" />
                  {excludedColCount} hidden
                </span>
              )}
            </div>
            {invoiceSummary && (
              <>
                <div className="h-3 w-px bg-border" />
                <div className="flex items-center gap-1">
                  <Hash className="w-3 h-3 text-indigo-400" />
                  <span className="text-indigo-400">{invoiceSummary.uniqueGroups} invoices</span>
                </div>
              </>
            )}
            {activeMarginCount > 0 && (
              <>
                <div className="h-3 w-px bg-border" />
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3 text-emerald-400" />
                  <span className="text-emerald-400">{activeMarginCount} margin rules</span>
                </div>
              </>
            )}
            {anomalySummary && anomalySummary.totalFlagged > 0 && (
              <>
                <div className="h-3 w-px bg-border" />
                <div className="flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3 text-amber-400" />
                  <span className="text-amber-400">{anomalySummary.totalFlagged} flagged</span>
                </div>
              </>
            )}
            {blankRowBetweenInvoices && (
              <>
                <div className="h-3 w-px bg-border" />
                <div className="flex items-center gap-1">
                  <Rows3 className="w-3 h-3 text-indigo-400" />
                  <span className="text-indigo-400">
                    blank rows by {BLANK_ROW_GROUP_OPTIONS.find(o => o.value === blankRowGroupByField)?.label || blankRowGroupByField}
                  </span>
                </div>
              </>
            )}

          </div>

          {/* ─── Override Blocks Export Warning ──────────────────── */}
          {overrideBlocksExport && (
            <div className="flex items-start gap-2 bg-amber-900/10 border border-amber-700/30 rounded-md px-3 py-2">
              <ShieldAlert className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-amber-400 font-medium">
                  Re-export requires confirmation
                </p>
                <p className="text-[10px] text-amber-400/70 mt-0.5">
                  {!hasOverrideReason
                    ? 'Please provide a reason for re-exporting previously exported jobs in the section above.'
                    : 'Please check the confirmation box in the section above to proceed.'
                  }
                </p>
              </div>
            </div>
          )}

          {/* ─── Validate Button ────────────────────────────────── */}
          <Button
            onClick={handleValidate}
            variant="outline"
            className="w-full border-border text-foreground hover:bg-secondary/50"
            disabled={checkingInvoiced || overrideBlocksExport}
          >
            <Shield className="w-4 h-4 mr-2 text-blue-400" />
            Run Export Validation
          </Button>

          {/* ─── Validation Results ─────────────────────────────── */}
          {validation && (
            <div className="border border-border/60 rounded-md overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-3 py-2 bg-secondary/20 hover:bg-secondary/30 transition-colors"
                onClick={() => setValidationExpanded(!validationExpanded)}
              >
                <div className="flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-blue-400" />
                  <span className="text-xs font-semibold text-foreground">Validation Results</span>
                  {validation.isValid ? (
                    <Badge className="bg-green-900/30 text-green-400 border-green-700/40 text-[9px] px-1.5 py-0">
                      <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Passed
                    </Badge>
                  ) : (
                    <Badge className="bg-red-900/30 text-red-400 border-red-700/40 text-[9px] px-1.5 py-0">
                      <XCircle className="w-2.5 h-2.5 mr-0.5" /> Blocked
                    </Badge>
                  )}
                  {validation.warnings.length > 0 && (
                    <Badge className="bg-amber-900/30 text-amber-400 border-amber-700/40 text-[9px] px-1.5 py-0">
                      {validation.warnings.length} warning{validation.warnings.length !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
                {validationExpanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
              </button>

              {validationExpanded && (
                <div className="px-3 py-2 space-y-1.5">
                  {validation.errors.length === 0 && validation.warnings.length === 0 && (
                    <div className="flex items-center gap-2 text-xs text-green-400 py-1">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      All checks passed. Ready to export.
                    </div>
                  )}

                  {validation.errors.map((err, i) => (
                    <div key={`e-${i}`} className="flex items-start gap-2 bg-red-900/10 border border-red-700/20 rounded px-2.5 py-1.5">
                      <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-red-400 font-medium">{err.message}</p>
                        {err.detail && <p className="text-[10px] text-red-400/70 mt-0.5">{err.detail}</p>}

                        {/* ─── Part 18: Bypass checkbox for MISSING_INVOICES ─── */}
                        {/* Lets the user choose to continue without fresh invoice
                            numbers (e.g. for re-exporting previously-invoiced
                            rows, or running a test/dry-run export).
                            Part 18.1: Fixed click handling — the outer div's
                            onClick was toggling state AND the inner Checkbox was
                            also toggling state (double-toggle → no-op when
                            clicking directly on the checkbox). Now the Checkbox
                            stops propagation and a useEffect re-runs validation
                            whenever `continueWithoutInvoices` changes. */}
                        {err.code === 'MISSING_INVOICES' && (
                          <div
                            className="mt-2 flex items-start gap-2 bg-amber-900/10 border border-amber-700/30 rounded px-2 py-1.5 cursor-pointer hover:bg-amber-900/15 transition-colors select-none"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setContinueWithoutInvoices(v => !v);
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === ' ' || e.key === 'Enter') {
                                e.preventDefault();
                                setContinueWithoutInvoices(v => !v);
                              }
                            }}
                          >
                            <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0 mt-0.5">
                              <Checkbox
                                checked={continueWithoutInvoices}
                                onCheckedChange={(v) => {
                                  setContinueWithoutInvoices(!!v);
                                }}
                                className="h-3.5 w-3.5"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] text-amber-400 font-medium">
                                Continue without invoice numbers
                              </p>
                              <p className="text-[9px] text-amber-400/70 mt-0.5">
                                Tick this to bypass the invoice check and export anyway — useful when re-exporting previously-invoiced rows (their existing invoice numbers will still be included) or running a test export.
                              </p>
                            </div>
                          </div>
                        )}

                      </div>
                    </div>
                  ))}


                  {validation.warnings.map((warn, i) => (
                    <div key={`w-${i}`} className={`flex items-start gap-2 rounded px-2.5 py-1.5 ${
                      warn.code === 'REINVOICE_OVERRIDE'
                        ? 'bg-amber-900/10 border border-amber-700/20'
                        : 'bg-amber-900/10 border border-amber-700/20'
                    }`}>
                      {warn.code === 'REINVOICE_OVERRIDE' ? (
                        <RefreshCw className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p className="text-xs font-medium text-amber-400">
                          {warn.message}
                        </p>
                        {warn.detail && (
                          <p className="text-[10px] mt-0.5 text-amber-400/70">
                            {warn.detail}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}

                  <div className="flex items-center gap-3 pt-1 text-[10px] text-muted-foreground border-t border-border/30 mt-1">
                    <span>Final rows: <span className="text-foreground font-medium">{validation.finalRowCount}</span></span>
                    <span>Final columns: <span className="text-foreground font-medium">{validation.finalColumnCount}</span></span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── Export Summary ──────────────────────────────────── */}
          {summary && (
            <div className="border border-border/60 rounded-md overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-3 py-2 bg-secondary/20 hover:bg-secondary/30 transition-colors"
                onClick={() => setSummaryExpanded(!summaryExpanded)}
              >
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-cyan-400" />
                  <span className="text-xs font-semibold text-foreground">Export Summary</span>
                </div>
                {summaryExpanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
              </button>

              {summaryExpanded && (
                <div className="px-3 py-2">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Template:</span>
                      <span className="text-foreground font-medium truncate ml-2">{summary.templateName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">File:</span>
                      <span className="text-foreground font-medium truncate ml-2">{summary.fileName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Rows:</span>
                      <span className="text-foreground font-medium">{summary.rowCount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Columns:</span>
                      <span className="text-foreground font-medium">{summary.columnCount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Scope:</span>
                      <span className="text-foreground font-medium">{EXPORT_SCOPE_INFO[summary.scope].label}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Grand Total:</span>
                      <span className="text-foreground font-medium">${summary.totalGrandTotal.toFixed(2)}</span>
                    </div>
                    {summary.startingInvoice !== null && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Invoice Range:</span>
                          <span className="text-indigo-400 font-medium">{summary.startingInvoice} - {summary.endingInvoice}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Invoice Groups:</span>
                          <span className="text-indigo-400 font-medium">{summary.invoiceGroupCount}</span>
                        </div>
                      </>
                    )}
                    {summary.activeMarginRules > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Margin Rules:</span>
                        <span className="text-emerald-400 font-medium">{summary.activeMarginRules} active</span>
                      </div>
                    )}
                    {(summary.anomalyIncluded > 0 || summary.anomalyExcluded > 0) && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Anomalies Included:</span>
                          <span className="text-amber-400 font-medium">{summary.anomalyIncluded}</span>
                        </div>
                        {summary.anomalyExcluded > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Anomalies Excluded:</span>
                            <span className="text-red-400 font-medium">{summary.anomalyExcluded}</span>
                          </div>
                        )}
                      </>
                    )}
                    {alreadyInvoiced.length > 0 && (
                      <>
                        {excludedInvoicedSet.size > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Prev. Exported Excluded:</span>
                            <span className="text-orange-400 font-medium">{excludedInvoicedSet.size}</span>
                          </div>
                        )}
                        {overriddenCount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Re-exported (file only):</span>
                            <span className="text-amber-400 font-medium">{overriddenCount} job{overriddenCount !== 1 ? 's' : ''}</span>
                          </div>
                        )}
                        {overriddenCount > 0 && newJobCount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">New invoice records:</span>
                            <span className="text-green-400 font-medium">{newJobCount} job{newJobCount !== 1 ? 's' : ''}</span>
                          </div>
                        )}
                      </>
                    )}
                    {blankRowBetweenInvoices && (
                      <div className="flex justify-between col-span-2">
                        <span className="text-muted-foreground">Blank Row Separators:</span>
                        <span className="text-indigo-400 font-medium">
                          Between each {BLANK_ROW_GROUP_OPTIONS.find(o => o.value === blankRowGroupByField)?.label || blankRowGroupByField} group
                        </span>
                      </div>
                    )}

                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── Success Message ─────────────────────────────────── */}
          {exported && (
            <div className={`flex items-center gap-2 rounded-md px-3 py-2 ${
              overriddenCount > 0
                ? 'bg-amber-900/15 border border-amber-700/30'
                : 'bg-green-900/15 border border-green-700/30'
            }`}>
              <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${overriddenCount > 0 ? 'text-amber-400' : 'text-green-400'}`} />
              <div>
                <p className={`text-xs font-medium ${overriddenCount > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                  Export downloaded and logged successfully
                  {overriddenCount > 0 && ` (${overriddenCount} re-exported, no new invoice records)`}
                </p>
                <p className={`text-[10px] ${overriddenCount > 0 ? 'text-amber-400/70' : 'text-green-400/70'}`}>
                  {summary?.fileName} — {summary?.rowCount} rows, {summary?.columnCount} columns
                  {blankRowBetweenInvoices && hasInvoices ? ' (with blank row separators)' : ''}
                  {overriddenCount > 0 && ` — Re-export reason logged`}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ─── Footer Actions ───────────────────────────────────── */}
        <DialogFooter className="mt-4 flex items-center gap-2">
          <Button variant="ghost" onClick={onClose} className="text-muted-foreground">
            {exported ? 'Done' : 'Cancel'}
          </Button>

          {!validation && (
            <Button
              onClick={handleValidate}
              className="golden-button"
              disabled={checkingInvoiced || overrideBlocksExport}
            >
              <Shield className="w-4 h-4 mr-1" />
              Validate & Review
            </Button>
          )}

          {validation && !validation.isValid && (
            <Button disabled className="bg-red-900/30 text-red-400 border border-red-700/40 cursor-not-allowed">
              <XCircle className="w-4 h-4 mr-1" />
              Export Blocked
            </Button>
          )}

          {validation && validation.isValid && !overrideBlocksExport && (
            <Button
              onClick={handleExport}
              disabled={exporting}
              className={overriddenCount > 0
                ? 'bg-amber-600 hover:bg-amber-700 text-white border border-amber-500'
                : 'golden-button'
              }
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : overriddenCount > 0 ? (
                <RefreshCw className="w-4 h-4 mr-1" />
              ) : (
                <Download className="w-4 h-4 mr-1" />
              )}
              {exporting
                ? 'Generating...'
                : overriddenCount > 0
                  ? `Re-export ${fileType.toUpperCase()} (${overriddenCount} re-included)`
                  : `Download ${fileType.toUpperCase()}`
              }
            </Button>
          )}

          {validation && validation.isValid && overrideBlocksExport && (
            <Button disabled className="bg-amber-900/30 text-amber-400 border border-amber-700/40 cursor-not-allowed">
              <ShieldAlert className="w-4 h-4 mr-1" />
              Confirmation Required
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportPanel;
